import React, { useState } from "react";
import {
  Box,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
} from "@chakra-ui/react";
import GarrazCopyText from "./GaraazCopyText";

interface GaraazTextComponentProps {
  header?: string;
  secondaryHeader?: string;
  content: string;
  maxNoOfLines?: number;
  maxNoOfCharacters?: number;
  style?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  secondaryHeaderStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  isHorizontal?: boolean;
  copyable?: boolean;
}

const GaraazTextComponent: React.FC<GaraazTextComponentProps> = ({
  header,
  secondaryHeader,
  content,
  maxNoOfLines = 4,
  maxNoOfCharacters = 350,
  style,
  headerStyle,
  secondaryHeaderStyle,
  contentStyle,
  isHorizontal = false,
  copyable,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onOpen = () => setIsModalOpen(true);
  const onClose = () => setIsModalOpen(false);

  const contentLines = content.split("\n").map((line, index) => {
    const isNumbered = /^\d+\.\s/.test(line.trim());
    return isNumbered ? line : `${index + 1}. ${line.trim()}`;
  });
  const isContentTruncated =
    contentLines.length > maxNoOfLines || content.length > maxNoOfCharacters;

  return (
    <Box
      width="100%"
      marginBottom={0.5}
      display={isHorizontal ? "flex" : ""}
      alignItems={"center"}
      style={style}
    >
      {header && (
        <Text
          style={headerStyle}
          fontWeight="bold"
          mb={"auto"}
          color={headerStyle?.color || "#0096e0"}
        >
          {header}
        </Text>
      )}

      {isHorizontal && (
        <Text
          mr={1}
          my={0}
          fontWeight={"bold"}
          color={headerStyle?.color || "#0096e0"}
        >
          :
        </Text>
      )}

      {secondaryHeader && (
        <Text
          style={secondaryHeaderStyle}
          fontWeight="medium"
          mb={1}
          color={secondaryHeaderStyle?.color || "#333"}
        >
          {secondaryHeader}
        </Text>
      )}

      {secondaryHeader && isHorizontal && (
        <Text
          mr={1}
          my={0}
          fontWeight={"bold"}
          color={secondaryHeaderStyle?.color || "#333"}
        >
          :
        </Text>
      )}

      <Text
        cursor={isContentTruncated ? "pointer" : "default"}
        noOfLines={maxNoOfLines}
        onClick={isContentTruncated ? onOpen : undefined}
        style={contentStyle}
        color={contentStyle?.color || "#555"}
      >
        {contentLines.length > 1
          ? contentLines.map((line, index) => <Text key={index}>{line}</Text>)
          : content}
      </Text>

      {copyable && (
        <GarrazCopyText
          text={contentLines.length > 1 ? contentLines.join("\n") : content}
          popOverMessage={`${header || "Text"} Copied`}
        ></GarrazCopyText>
      )}

      {isContentTruncated && (
        <Modal isOpen={isModalOpen} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{header || "Content Details"}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {secondaryHeader && (
                <Text
                  style={secondaryHeaderStyle}
                  fontWeight="medium"
                  color={secondaryHeaderStyle?.color || "#333"}
                >
                  {secondaryHeader}
                </Text>
              )}
              {contentLines.map((line, index) => (
                <Text key={index} mb={1}>
                  {line}
                </Text>
              ))}
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" onClick={onClose}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default GaraazTextComponent;
